import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Badge,
    Box,
    Divider,
    IconButton,
    ListSubheader,
    MenuItem,
    MenuList,
    Popover,
    Tooltip
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DensityLargeIcon from '@mui/icons-material/DensityLarge';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import i18n from '../../../i18n';
import { DENSITY_ORDER } from '../../../theme/density';
import {
    hiddenColumnsButtonSx,
    hiddenColumnsItemSx,
    hiddenColumnsOptionSx,
    hiddenColumnsPaperSx,
    hiddenColumnsShowSx,
    hiddenColumnsTitleSx
} from './GridHiddenColumnsButton.styles';

const DENSITY_ICONS = {
    compact: DensitySmallIcon,
    standard: DensityMediumIcon,
    comfortable: DensityLargeIcon
};

/**
 * The columns button in the head of the actions column: it brings back
 * columns the user hid — dropped on the remove zone or unticked in table
 * settings — sets the row height, and resets the column widths and order.
 * It carries the count of hidden columns; its menu lists them, each shown
 * again on a click, offers to show them all, offers the row heights, and
 * the layout reset while there is something to reset.
 * Presentation only: the grid says what to offer and does the work.
 *
 * @param {Object} props - Component props.
 * @param {Array<{id: string, title: string}>} props.columns - Hidden
 *   columns, in Head order.
 * @param {Function} props.onShow - `(ids)` shows these columns again.
 * @param {Function} [props.onResetLayout] - Resets the column widths and
 *   order; left out while there is nothing to reset.
 * @param {string} [props.density] - The current row height, one of
 *   DENSITY_ORDER (theme/density.js).
 * @param {Function} [props.onDensityChange] - `(density)` sets the row
 *   height; left out, the menu offers none.
 * @returns {JSX.Element|null} the button, or nothing with nothing to offer
 */
export default function GridHiddenColumnsButton({
    columns,
    onShow,
    onResetLayout,
    density,
    onDensityChange
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const isEmpty = !columns.length && !onResetLayout && !onDensityChange;

    // Left with nothing to offer, the menu closes, or it would open again by
    // itself, on a stale anchor, once there is something.
    useEffect(() => {
        if (isEmpty) setAnchorEl(null);
    }, [isEmpty]);

    if (isEmpty) return null;

    const close = () => setAnchorEl(null);
    const label = i18n.t('common.columnsMenu');
    const hiddenLabel = i18n.t('common.hiddenColumns');

    // One section per kind of setting, with a divider between them; plain
    // children, as MenuList wants them for keyboard navigation.
    const sections = [];
    if (columns.length) {
        const items = [
            <ListSubheader key="hidden-title" disableSticky sx={hiddenColumnsTitleSx}>
                {hiddenLabel}
            </ListSubheader>,
            ...columns.map((column) => (
                <MenuItem
                    key={column.id}
                    sx={hiddenColumnsItemSx}
                    onClick={() => {
                        // The last one takes its section, and the item that
                        // has focus, with it: the menu closes, and focus goes
                        // back to the button.
                        if (columns.length === 1) close();
                        onShow([column.id]);
                    }}
                    data-testid={`grid-hidden-columns-show-${column.id}`}
                >
                    <span>{column.title}</span>
                    <Box component="span" sx={hiddenColumnsShowSx}>
                        <VisibilityOutlinedIcon fontSize="inherit" />
                        {i18n.t('common.showColumn')}
                    </Box>
                </MenuItem>
            ))
        ];
        if (columns.length > 1) {
            items.push(
                <MenuItem
                    key="show-all"
                    sx={hiddenColumnsItemSx}
                    onClick={() => {
                        close();
                        onShow(columns.map((column) => column.id));
                    }}
                    data-testid="grid-hidden-columns-show-all"
                >
                    {i18n.t('common.showAllColumns')}
                </MenuItem>
            );
        }
        sections.push(items);
    }
    if (onDensityChange) {
        sections.push([
            <ListSubheader key="density-title" disableSticky sx={hiddenColumnsTitleSx}>
                {i18n.t('common.density')}
            </ListSubheader>,
            ...DENSITY_ORDER.map((value) => {
                const Icon = DENSITY_ICONS[value];
                const selected = value === density;
                return (
                    <MenuItem
                        key={`density-${value}`}
                        selected={selected}
                        sx={hiddenColumnsOptionSx}
                        onClick={() => onDensityChange(value)}
                        data-testid={`grid-row-density-${value}`}
                    >
                        <Icon fontSize="small" />
                        <span>{i18n.t(`density.${value}`)}</span>
                        {selected && <CheckIcon fontSize="small" />}
                    </MenuItem>
                );
            })
        ]);
    }
    if (onResetLayout) {
        sections.push([
            <MenuItem
                key="reset-layout"
                sx={hiddenColumnsOptionSx}
                onClick={() => {
                    close();
                    onResetLayout();
                }}
                data-testid="grid-columns-reset-layout"
            >
                <RestartAltIcon fontSize="small" />
                <span>{i18n.t('common.resetColumnLayout')}</span>
            </MenuItem>
        ]);
    }

    return (
        <>
            <Tooltip title={label}>
                <IconButton
                    size="small"
                    aria-label={
                        columns.length
                            ? `${label}. ${hiddenLabel}: ${columns.length}`
                            : label
                    }
                    aria-haspopup="menu"
                    aria-expanded={Boolean(anchorEl)}
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                    sx={hiddenColumnsButtonSx}
                    data-testid="grid-hidden-columns-button"
                >
                    <Badge badgeContent={columns.length} color="primary">
                        <ViewColumnOutlinedIcon />
                    </Badge>
                </IconButton>
            </Tooltip>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: hiddenColumnsPaperSx } }}
            >
                <MenuList autoFocusItem dense aria-label={label}>
                    {sections.flatMap((items, index) =>
                        index === 0
                            ? items
                            : [<Divider key={`divider-${index}`} />, ...items]
                    )}
                </MenuList>
            </Popover>
        </>
    );
}

GridHiddenColumnsButton.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired
        })
    ).isRequired,
    onShow: PropTypes.func.isRequired,
    onResetLayout: PropTypes.func,
    density: PropTypes.oneOf(DENSITY_ORDER),
    onDensityChange: PropTypes.func
};
